import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Phone, Calendar, Info, Clock, ArrowRightLeft, XCircle, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { statusLabel, CHANNEL_TR } from '../i18n/tr';
import { resolveDisplayStatus } from '../utils/callStatus';
import PageHeader from './ui/PageHeader';
import StatCard from './ui/StatCard';
import EmptyState from './ui/EmptyState';
import StatusBadge from './ui/StatusBadge';
import { useTheme } from '../contexts/ThemeContext';

const DAY_MS = 24 * 60 * 60 * 1000;

const TIME_RANGES = [
  { id: 'all', label: 'Tüm zamanlar', windowMs: null },
  { id: 'month', label: 'Bu ay', windowMs: 30 * DAY_MS },
  { id: 'week', label: 'Bu hafta', windowMs: 7 * DAY_MS },
  { id: 'day', label: 'Bugün', windowMs: DAY_MS },
];

const Dashboard = ({ callLogs = [] }) => {
  const { isLight } = useTheme();
  const chartColors = isLight
    ? {
        axis: '#64748b',
        axisLine: 'rgba(15,23,42,0.15)',
        grid: 'rgba(15,23,42,0.08)',
        cursor: 'rgba(15,23,42,0.05)',
        tooltipBg: '#ffffff',
        tooltipBorder: 'rgba(15,23,42,0.12)',
        tooltipText: '#1e293b',
        pieStroke: '#ffffff',
      }
    : {
        axis: '#94a3b8',
        axisLine: 'rgba(255,255,255,0.15)',
        grid: 'rgba(255,255,255,0.08)',
        cursor: 'rgba(255,255,255,0.04)',
        tooltipBg: '#1a1d27',
        tooltipBorder: 'rgba(255,255,255,0.09)',
        tooltipText: '#d4d8e2',
        pieStroke: 'rgba(5,7,10,0.6)',
      };

  const [rangeId, setRangeId] = useState('all');
  const [rangeOpen, setRangeOpen] = useState(false);
  const rangeRef = useRef(null);

  const activeRange = TIME_RANGES.find((r) => r.id === rangeId) || TIME_RANGES[0];

  useEffect(() => {
    if (!rangeOpen) return undefined;
    const handleClick = (e) => {
      if (rangeRef.current && !rangeRef.current.contains(e.target)) {
        setRangeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [rangeOpen]);

  const filteredCalls = useMemo(() => {
    if (!activeRange.windowMs) return callLogs;
    const cutoff = Date.now() - activeRange.windowMs;
    return callLogs.filter((call) => {
      const t = new Date(call.callTime).getTime();
      return !Number.isNaN(t) && t >= cutoff;
    });
  }, [callLogs, activeRange]);

  const stats = useMemo(() => {
    const resolved = filteredCalls.map((call) => ({
      ...call,
      displayStatus: resolveDisplayStatus(call),
    }));

    return {
      total: resolved.length,
      appointments: resolved.filter((c) => c.displayStatus === 'Appointment Created').length,
      appointmentRequested: resolved.filter((c) => c.displayStatus === 'Appointment Requested').length,
      routed: resolved.filter((c) => c.displayStatus === 'Routed to MTS/SKS').length,
      transferred: resolved.filter((c) => c.displayStatus === 'Transferred').length,
      infoProvided: resolved.filter((c) => c.displayStatus === 'Info Provided').length,
      failed: resolved.filter((c) => c.displayStatus === 'Failed').length,
    };
  }, [filteredCalls]);

  const chartData = useMemo(() => {
    return [
      { name: statusLabel('Appointment Created'), value: stats.appointments, color: '#4a9e8a' },
      { name: statusLabel('Routed to MTS/SKS'), value: stats.routed, color: '#fbbf24' },
      { name: statusLabel('Transferred'), value: stats.transferred, color: '#818cf8' },
      { name: statusLabel('Info Provided'), value: stats.infoProvided, color: '#a78bfa' },
      { name: statusLabel('Failed'), value: stats.failed, color: '#f87171' },
    ].filter((d) => d.value > 0);
  }, [stats]);

  const recentCalls = useMemo(() => filteredCalls.slice(0, 10), [filteredCalls]);

  const displayPhone = (call) => {
    if (call.channel && CHANNEL_TR[call.channel]) {
      return call.appointment?.customerPhone || CHANNEL_TR[call.channel];
    }
    return call.phoneNumber || '—';
  };

  const liveBadge = (
    <div className="flex items-center rounded-lg bg-emerald-400/10 px-3 py-2 ring-1 ring-emerald-400/20">
      <div className="mr-2 h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
      <span className="text-sm font-medium text-emerald-300">Canlı</span>
    </div>
  );

  const rangeDropdown = (
    <span ref={rangeRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setRangeOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={rangeOpen}
        className="inline-flex items-center gap-1 rounded-lg bg-white/[0.04] px-2.5 py-1 text-xs font-medium text-slate-300 ring-1 ring-white/10 transition-colors hover:bg-white/[0.08] hover:text-white"
      >
        {activeRange.label}
        <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${rangeOpen ? 'rotate-180' : ''}`} />
      </button>
      {rangeOpen && (
        <span
          role="listbox"
          className="absolute left-0 top-full z-20 mt-1 flex min-w-[9rem] flex-col rounded-xl border border-white/10 bg-[#1a1d27] p-1 shadow-xl shadow-black/40"
        >
          {TIME_RANGES.map((range) => (
            <button
              key={range.id}
              type="button"
              role="option"
              aria-selected={range.id === rangeId}
              onClick={() => {
                setRangeId(range.id);
                setRangeOpen(false);
              }}
              className={`rounded-lg px-3 py-1.5 text-left text-xs font-medium transition-colors ${
                range.id === rangeId
                  ? 'bg-emerald-400/15 text-emerald-200'
                  : 'text-slate-300 hover:bg-white/[0.06] hover:text-white'
              }`}
            >
              {range.label}
            </button>
          ))}
        </span>
      )}
    </span>
  );

  const headerDescription = (
    <>
      <span>
        {`Mengerler Yenimahalle — Dijital Randevu Asistanı — ${format(new Date(), 'd MMMM yyyy', {
          locale: tr,
        })}`}
      </span>
      {rangeDropdown}
    </>
  );

  return (
    <div className="page-container">
      <PageHeader
        title="Çağrı Özet Paneli"
        description={headerDescription}
        badge={liveBadge}
      />

      <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard title="Toplam çağrı" value={stats.total} icon={Phone} accent="blue" isLive />
        <StatCard
          title="Randevu oluşturuldu"
          value={stats.appointments}
          icon={Calendar}
          accent="purple"
          isLive
        />
        <StatCard title="MTS/SKS yönlendirme" value={stats.routed} icon={ArrowRightLeft} accent="amber" isLive />
        <StatCard title="Aktarıldı" value={stats.transferred} icon={ArrowRightLeft} accent="indigo" isLive />
        <StatCard title="Bilgi verildi" value={stats.infoProvided} icon={Info} accent="orange" isLive />
        <StatCard title="Başarısız" value={stats.failed} icon={XCircle} accent="green" isLive />
      </div>

      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="card">
          <h3 className="mb-4 text-lg font-semibold text-white">Çağrı durum dağılımı</h3>
          {chartData.length === 0 ? (
            <p className="py-12 text-center text-sm text-slate-400">Henüz çağrı verisi yok</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: chartColors.axis }} stroke={chartColors.axisLine} />
                <YAxis allowDecimals={false} tick={{ fill: chartColors.axis }} stroke={chartColors.axisLine} />
                <Tooltip
                  cursor={{ fill: chartColors.cursor }}
                  contentStyle={{
                    background: chartColors.tooltipBg,
                    border: `1px solid ${chartColors.tooltipBorder}`,
                    borderRadius: 6,
                    color: chartColors.tooltipText,
                  }}
                />
                <Bar dataKey="value" fill="#4a9e8a" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card">
          <h3 className="mb-4 text-lg font-semibold text-white">Sonuç oranları</h3>
          {chartData.length === 0 ? (
            <p className="py-12 text-center text-sm text-slate-400">Henüz çağrı verisi yok</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} %${(percent * 100).toFixed(0)}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  stroke={chartColors.pieStroke}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: chartColors.tooltipBg,
                    border: `1px solid ${chartColors.tooltipBorder}`,
                    borderRadius: 6,
                    color: chartColors.tooltipText,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="card">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Son çağrılar</h3>
          <div className="flex items-center text-sm text-slate-300">
            <Clock className="mr-1 h-4 w-4" />
            {recentCalls.length > 0 ? `Son ${recentCalls.length} çağrı` : 'Henüz çağrı yok'}
          </div>
        </div>

        {recentCalls.length === 0 ? (
          <EmptyState
            icon={Phone}
            title="Henüz çağrı kaydı yok"
            description="Sesli asistan veya telefon hattından gelen görüşmeler burada listelenir."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                    Saat
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                    Kaynak / Telefon
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                    Süre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-300">
                    Özet
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {recentCalls.map((call, index) => (
                  <tr
                    key={call.id}
                    className={`transition-colors hover:bg-white/5 ${index === 0 ? 'bg-emerald-400/[0.07]' : ''}`}
                  >
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-200">
                      {format(new Date(call.callTime), 'HH:mm:ss')}
                      {index === 0 && (
                        <span className="ml-2 inline-flex items-center rounded bg-emerald-400/15 px-2 py-0.5 text-xs font-medium text-emerald-300 ring-1 ring-emerald-400/20">
                          En yeni
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-200">
                      {displayPhone(call)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-300">{call.duration}</td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <StatusBadge call={call} />
                    </td>
                    <td className="max-w-xs truncate px-6 py-4 text-sm text-slate-300">{call.summary}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
